import { h } from 'preact'
import Phrase from '../../../libs/translationsUtils/phrase'
import SearchResultsSection from './SearchResultsSection'
import HitContainerHeader from './HitContainerHeader'
import { DoSearch, GetItemPropsFn, AllSearchResults, SearchResults, isMultiIndexResult } from './index'
import SearchActionLink from './SearchActionLink'

interface Props {
  key?: string
  indexName: string
  allSearchResults: SearchResults | AllSearchResults | null
  inputValue: string
  doSearch: DoSearch
  highlightedIndex: number | null
  previousResultsCount?: number
  showFooterLinks?: boolean
  getItemProps: GetItemPropsFn
  style?: CSSStyleDeclaration
  setHighlightedIndex: (idx: number) => void
}

const HitContainerWithFederated = ({
  key,
  indexName,
  allSearchResults,
  inputValue,
  doSearch,
  highlightedIndex,
  showFooterLinks,
  getItemProps,
  setHighlightedIndex,
  style,
  previousResultsCount = 0
}: Props) => {
  let communitySearchMetadata: SearchResults | undefined
  let zendeskSearchMetadata: SearchResults | undefined
  let salesforceSearchMetadata: SearchResults | undefined
  let skilljarSearchMetadata: SearchResults | undefined
  let freshdeskSearchMetadata: SearchResults | undefined

  if (isMultiIndexResult(allSearchResults)) {
    communitySearchMetadata = allSearchResults?.[indexName]
    zendeskSearchMetadata = allSearchResults?.[`${indexName}-zendesk`]
    salesforceSearchMetadata = allSearchResults?.[`${indexName}-salesforce`]
    skilljarSearchMetadata = allSearchResults?.[`${indexName}-skilljar`]
    freshdeskSearchMetadata = allSearchResults?.[`${indexName}-freshdesk`]
  } else {
    communitySearchMetadata = allSearchResults ?? undefined
  }

  const communityHits = communitySearchMetadata ? communitySearchMetadata.hits : []
  const zendeskHits = zendeskSearchMetadata ? zendeskSearchMetadata.hits : []
  const salesforceHits = salesforceSearchMetadata ? salesforceSearchMetadata.hits : []
  const skilljarHits = skilljarSearchMetadata ? skilljarSearchMetadata.hits : []
  const freshdeskHits = freshdeskSearchMetadata ? freshdeskSearchMetadata.hits : []

  const hasResults = communityHits.length || zendeskHits.length || salesforceHits.length || skilljarHits.length || freshdeskHits.length

  return (
    <div key={`${key ?? 'results'}`} className='algolia-hit-container' style={style}>
      <HitContainerHeader
        inputValue={inputValue}
        hasResults={!!hasResults}
        doSearch={doSearch}
      />

      {communitySearchMetadata && communityHits
        ? <SearchResultsSection
            title={isMultiIndexResult(allSearchResults) ? <Phrase p='nav.title.community' /> : ''}
            type='community'
            searchMetadata={communitySearchMetadata}
            hits={communityHits}
            highlightedIndex={highlightedIndex}
            getItemProps={getItemProps}
            previousResultsCount={previousResultsCount}
            setHighlightedIndex={setHighlightedIndex}
            viewAllUrl={`/search?q=${inputValue}`}
          />
        : null}

      {zendeskSearchMetadata && zendeskHits && communityHits
        ? <SearchResultsSection
            title={<Phrase p='federated.search.zendesk' />}
            type='zendesk'
            searchMetadata={zendeskSearchMetadata}
            hits={zendeskHits}
            highlightedIndex={highlightedIndex}
            getItemProps={getItemProps}
            setHighlightedIndex={setHighlightedIndex}
            previousResultsCount={previousResultsCount + communityHits.length}
            viewAllUrl={`/search?source=zendesk&q=${inputValue}`}
          />
        : null}

      {salesforceSearchMetadata && salesforceHits && communityHits
        ? <SearchResultsSection
            title={<Phrase p='federated.search.salesforce' />}
            type='salesforce'
            searchMetadata={salesforceSearchMetadata}
            hits={salesforceHits}
            highlightedIndex={highlightedIndex}
            getItemProps={getItemProps}
            setHighlightedIndex={setHighlightedIndex}
            previousResultsCount={previousResultsCount + zendeskHits.length + communityHits.length}
            viewAllUrl={`/search?source=salesforce&q=${inputValue}`}
          />
        : null}

      {skilljarSearchMetadata && skilljarHits && communityHits
        ? <SearchResultsSection
            title={<Phrase p='federated.search.skilljar' />}
            type='skilljar'
            searchMetadata={skilljarSearchMetadata}
            hits={skilljarHits}
            highlightedIndex={highlightedIndex}
            getItemProps={getItemProps}
            setHighlightedIndex={setHighlightedIndex}
            previousResultsCount={previousResultsCount + salesforceHits.length + zendeskHits.length + communityHits.length}
            viewAllUrl={`/search?source=skilljar&q=${inputValue}`}
          />
        : null}

      {freshdeskSearchMetadata && freshdeskHits && communityHits
        ? <SearchResultsSection
            title={<Phrase p='federated.search.freshdesk' />}
            type='freshdesk'
            searchMetadata={freshdeskSearchMetadata}
            hits={freshdeskHits}
            highlightedIndex={highlightedIndex}
            getItemProps={getItemProps}
            setHighlightedIndex={setHighlightedIndex}
            previousResultsCount={previousResultsCount + skilljarHits.length + salesforceHits.length + zendeskHits.length + communityHits.length}
            viewAllUrl={`/search?source=freshdesk&q=${inputValue}`}
          />
        : null}

      {!isMultiIndexResult(allSearchResults) && showFooterLinks
        ? <SearchActionLink icon='search' href={`/search?q=${inputValue}`}>
          <Phrase p='live.search.view.all' />
        </SearchActionLink>
        : null}

      {showFooterLinks
        ? <SearchActionLink icon='edit' href='/topic/new'>
          <Phrase p='live.search.ask.question' />
        </SearchActionLink>
        : null}
    </div>
  )
}

export default HitContainerWithFederated
